/**
 * Subscription onboarding wizard — kalkuluje zapotrzebowanie psa na karmę
 * i koszt subskrypcji (-10%).
 *
 * Markup: templates/subscription-page.php. Bez REST — kalkulacja client-side.
 *
 * Model zapotrzebowania:
 *   RER  = 70 × waga^0.75            (resting energy requirement, kcal/dzień)
 *   MER  = RER × 1.6 × activity      (maintenance — activity: 0.9 / 1.0 / 1.2)
 *   g/dzień = MER / kcal_per_kg × 1000
 *   kcal/kg: sucha ≈ 3600, mokra ≈ 1050
 */

const KCAL_PER_KG = { dry: 3600, wet: 1050 };
const MER_FACTOR = 1.6;          // adult maintenance
const SUBSCRIPTION_DISCOUNT = 0.10;
const DAYS_PER_MONTH = 30.4;

const pln = new Intl.NumberFormat('pl-PL', { style: 'currency', currency: 'PLN' });

export function initSubscription() {
    const wizard = document.querySelector('[data-hn-sub-wizard]');
    if (!wizard) {
        return;
    }

    const steps = Array.from(wizard.querySelectorAll('.hn-sub-step'))
        .sort((a, b) => Number(a.dataset.step) - Number(b.dataset.step));
    if (!steps.length) {
        return;
    }

    const progress    = wizard.querySelector('[data-hn-sub-progress]');
    const progressBox = progress ? progress.closest('.hn-sub-wizard__progress') : null;
    const result      = wizard.querySelector('[data-hn-sub-result]');
    const weightInput = wizard.querySelector('[data-hn-sub-weight]');
    const weightValue = wizard.querySelector('[data-hn-sub-weight-value]');

    const summaryEl    = wizard.querySelector('[data-hn-sub-summary]');
    const priceRegEl   = wizard.querySelector('[data-hn-sub-price-regular]');
    const priceDiscEl  = wizard.querySelector('[data-hn-sub-price-discounted]');
    const savingsEl    = wizard.querySelector('[data-hn-sub-savings]');

    let current = 0;

    function checkedRadio(name) {
        return wizard.querySelector('input[name="' + name + '"]:checked');
    }

    function checkedLabel(name) {
        const radio = checkedRadio(name);
        if (!radio) {
            return '';
        }
        const label = radio.closest('.hn-sub-step__option');
        const text = label ? label.querySelector('.hn-sub-step__option-label') : null;
        return text ? text.textContent.trim() : '';
    }

    function showStep(index) {
        current = Math.max(0, Math.min(index, steps.length - 1));
        steps.forEach((step, i) => { step.hidden = i !== current; });
        if (result) result.hidden = true;
        if (progressBox) progressBox.hidden = false;
        if (progress) {
            progress.style.width = Math.round(((current + 1) / steps.length) * 100) + '%';
        }
        wizard.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    function calculate() {
        const weight = Math.max(1, parseFloat(weightInput && weightInput.value) || 12);
        const activity = parseFloat((checkedRadio('activity') || {}).value) || 1.0;
        const foodRadio = checkedRadio('food_type');
        const foodType = foodRadio ? foodRadio.value : 'dry';
        const pricePerKg = foodRadio ? parseFloat(foodRadio.dataset.pricePerKg) : 22;
        const freqWeeks = parseInt((checkedRadio('freq') || {}).value, 10) || 4;

        const kcalPerKg = KCAL_PER_KG[foodType] || KCAL_PER_KG.dry;
        const rer = 70 * Math.pow(weight, 0.75);
        const mer = rer * MER_FACTOR * activity;
        const dailyG = (mer / kcalPerKg) * 1000;

        const monthlyKg = (dailyG * DAYS_PER_MONTH) / 1000;
        const perPackageKg = (dailyG * freqWeeks * 7) / 1000;
        const regular = monthlyKg * pricePerKg;
        const discounted = regular * (1 - SUBSCRIPTION_DISCOUNT);
        const savings = regular - discounted;

        return {
            weight,
            foodType,
            freqWeeks,
            activityLabel: checkedLabel('activity'),
            dailyG: Math.round(dailyG / 5) * 5,
            monthlyKg,
            perPackageKg,
            regular,
            discounted,
            savings,
        };
    }

    function renderResult() {
        const data = calculate();
        const foodLabel = data.foodType === 'wet' ? 'mokra' : 'sucha';

        if (summaryEl) {
            summaryEl.innerHTML =
                '<p><strong>Pies ' + data.weight + ' kg</strong>' +
                    (data.activityLabel ? ' · ' + data.activityLabel.toLowerCase() : '') + '</p>' +
                '<p>Karma ' + foodLabel + ' — ok. <strong>' + data.dailyG + ' g</strong> dziennie, ' +
                    '~<strong>' + data.monthlyKg.toFixed(1).replace('.', ',') + ' kg</strong> miesięcznie.</p>' +
                '<p>Paczka co ' + data.freqWeeks + ' tyg. ≈ ' +
                    data.perPackageKg.toFixed(1).replace('.', ',') + ' kg karmy.</p>';
        }
        if (priceRegEl)  priceRegEl.textContent  = pln.format(data.regular);
        if (priceDiscEl) priceDiscEl.textContent = pln.format(data.discounted);
        if (savingsEl)   savingsEl.textContent   = pln.format(data.savings);

        steps.forEach((step) => { step.hidden = true; });
        if (progressBox) progressBox.hidden = true;
        if (progress) progress.style.width = '100%';
        if (result) result.hidden = false;
        if (result) result.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    // Live update wartości wagi przy przeciąganiu suwaka.
    if (weightInput && weightValue) {
        weightInput.addEventListener('input', () => {
            weightValue.textContent = weightInput.value;
        });
    }

    // Delegacja — przyciski statyczne w obrębie wizarda.
    wizard.addEventListener('click', (event) => {
        const target = event.target.closest(
            '[data-hn-sub-next],[data-hn-sub-back],[data-hn-sub-finish],[data-hn-sub-restart]'
        );
        if (!target) {
            return;
        }
        if (target.hasAttribute('data-hn-sub-next')) {
            showStep(current + 1);
        } else if (target.hasAttribute('data-hn-sub-back')) {
            showStep(current - 1);
        } else if (target.hasAttribute('data-hn-sub-finish')) {
            renderResult();
        } else if (target.hasAttribute('data-hn-sub-restart')) {
            showStep(0);
        }
    });

    showStep(0);
}
